import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, AbstractControl } from '@angular/forms';

import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { Contract } from '../../model/contract';
import { Tenant } from '../../model/tenant';
import { TenantService } from '../../service/data/tenant.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { Observable, of, Subscription } from 'rxjs';
import { concatMap, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { FormControlEx } from '../../../core/component/forms/form-control';

@Component({
    selector: 'app-tenant-select',
    templateUrl: './tenant-autocomplete.component.html',
    styleUrls: ['./tenant-autocomplete.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TenantAutocompleteComponent implements OnInit, OnDestroy {
  navigation: any;
  constructor(
    public dataService: TenantService,
    private _fcf: FormControlExFactory,
    private _navigation: NavigationService
  ) {}

  ngOnDestroy(): void {
    if (this._subscription) this._subscription.unsubscribe();
  }

  @Input() dataContext: Contract;
  @Input() tenantCount = 2;
  @Input() form: FormGroup;
  @Output() tenantOneChange: EventEmitter<Tenant> = new EventEmitter<Tenant>();
  @Output() tenantTwoChange: EventEmitter<Tenant> = new EventEmitter<Tenant>();

  private _subscription = new Subscription();

  private _tenantOne: Tenant;
  private get tenantOne(): Tenant {
    return this._tenantOne;
  }
  private set tenantOne(tenant: Tenant) {
    this._tenantOne = tenant;
    this.tenantOneEmailControl.setValue(this.tenantOne?.email);
  }

  private _tenantTwo: Tenant;
  private get tenantTwo(): Tenant {
    return this._tenantTwo;
  }
  private set tenantTwo(tenant: Tenant) {
    this._tenantTwo = tenant;
    this.tenantTwoEmailControl.setValue(this.tenantTwo?.email);
  }

  get tenantOneCivic(): string {
    return this.tenantOne ? this.tenantOne.contactIdentification : '';
  }

  get tenantOneExist(): boolean {
    return this.form.controls['tenantOne'].value;
  }

  get tenantTwoExist(): boolean {
    return this.form.controls['tenantTwo'].value;
  }

  get tenantOneHasCivic(): boolean {
    return this.tenantOneCivic !== '';
  }

  get tenantOnePhone(): string {
    return this.tenantOne ? this.tenantOne.phone : '';
  }

  get tenantOneHasPhone(): boolean {
    return this.tenantOnePhone !== '';
  }

  get tenantOneIdType(): string {
    return 'TENANT.ID_TYPE.' + (this.tenantOne && this.tenantOne.idType ? `T${this.tenantOne.idType}` : 'ID_NUMBER');
  }

  get tenantOneEmail(): string {
    return this.tenantOne ? this.tenantOne.email : '';
  }

  get tenantOneHasEmail(): boolean {
    return this.tenantOneEmail !== '';
  }

  get tenantTwoCivic(): string {
    return this.tenantTwo ? this.tenantTwo.contactIdentification : '';
  }

  get tenantTwoHasCivic(): boolean {
    return this.tenantTwoCivic !== '';
  }

  get tenantTwoPhone(): string {
    return this.tenantTwo ? this.tenantTwo.phone : '';
  }

  get tenantTwoHasPhone(): boolean {
    return this.tenantTwoPhone !== '';
  }

  get tenantTwoIdType(): string {
    return 'TENANT.ID_TYPE.' + (this.tenantTwo && this.tenantTwo.idType ? `T${this.tenantTwo.idType}` : 'ID_NUMBER');
  }

  get tenantTwoEmail(): string {
    return this.tenantTwo ? this.tenantTwo.email : '';
  }

  get tenantTwoHasEmail(): boolean {
    return this.tenantTwoEmail !== '';
  }

  tenantOneEmailControl: FormControlEx;
  tenantTwoEmailControl: FormControlEx;
  ngOnInit() {
    this.tenantOneEmailControl = this._fcf.createReadonly('TENANT.EMAIL', null, 'TENANT.HELP_TEXT.OPEN_TENANT');
    this.tenantTwoEmailControl = this._fcf.createReadonly('TENANT.EMAIL', null, 'TENANT.HELP_TEXT.OPEN_TENANT');
    let tenantOneId: string = null;
    if (this.dataContext.tenantOne) {
      if (typeof this.dataContext.tenantOne !== 'string') {
        tenantOneId = this.dataContext.tenantOne.id;
        this.tenantOne = this.dataContext.tenantOne;
      } else {
        tenantOneId = this.dataContext.tenantOne;
        this._subscription.add(this.dataService.getById(tenantOneId).subscribe(tenant => (this.tenantOne = tenant)));
      }
    }
    let tenantTwoId: string = null;
    if (this.dataContext.tenantTwo) {
      if (typeof this.dataContext.tenantTwo !== 'string') {
        tenantTwoId = this.dataContext.tenantTwo.id;
        this.tenantTwo = this.dataContext.tenantTwo;
      } else {
        tenantTwoId = this.dataContext.tenantTwo;
        this._subscription.add(this.dataService.getById(tenantTwoId).subscribe(tenant => (this.tenantTwo = tenant)));
      }
    }

    this._subscription.add(
      this.form.controls['tenantOne'].valueChanges
        .pipe(switchMap(value => (value ? this.dataService.getById(value) : of(null as Tenant))))
        .subscribe(tenant => {
          this.tenantOne = tenant;
          this.tenantOneChange.emit(tenant);
        })
    );

    this._subscription.add(
      this.form.controls['tenantTwo'].valueChanges
        .pipe(switchMap(value => (value ? this.dataService.getById(value) : of(null as Tenant))))
        .subscribe(tenant => {
          this.tenantTwo = tenant;
          this.tenantTwoChange.emit(tenant);
        })
    );
  }

  onOpenTenantOneClicked(): void {
    if (this.tenantOneExist) {
      this._navigation.navigate(['tenants', 'edit', this.form.controls['tenantOne'].value]);
    }
  }

  onOpenTenantTwoClicked(): void {
    if (this.tenantTwoExist) {
      this._navigation.navigate(['tenants', 'edit', this.form.controls['tenantTwo'].value]);
    }
  }
}
